import { useMemo } from 'react';
import { Grid } from '@material-ui/core';
import { Input } from '@WESCO-International/wdp-ui-components/components/input';
import { Dropdown } from '@WESCO-International/wdp-ui-components/components/dropdown/Dropdown';
import {
  kindOptions,
  ownerOptions,
  componentTypeOptions,
  apiTypeOptions,
  domainOptions,
} from '../common/utils';
import { FormField } from '../common/FormField';
import type { DropdownOption, FormState, KindType } from '../common/types';

interface BasicDetailsSectionProps {
  kind: KindType;
  form: FormState;
  systemOptions: DropdownOption[];
  onKindChange: (kind: KindType) => void;
  onNameChange: (value: string) => void;
  onDomainChange: (domain: string) => void;
  onFieldChange: <K extends keyof FormState>(
    field: K,
    value: FormState[K],
  ) => void;
}

const toDisplayOptions = (options: DropdownOption[]) =>
  options.map(o => ({
    ...o,
    label: o.label || String(o.value),
    value: o.value,
  }));

export const BasicDetailsSection = ({
  kind,
  form,
  systemOptions,
  onKindChange,
  onNameChange,
  onDomainChange,
  onFieldChange,
}: BasicDetailsSectionProps) => {
  const componentTypeOptionsDisplay = useMemo(
    () => toDisplayOptions(componentTypeOptions),
    [],
  );

  return (
    <div className="formSection">
      <h6 className="wdp-heading s-6 semibold" style={{ marginBottom: 12 }}>
        Basic Details
      </h6>
      <Grid container spacing={2}>
        {kind === 'Component' ? (
          <>
            {/* Row 1 — Kind / Name / Owner */}
            <Grid item xs={12} md={4}>
              <FormField label="Kind" required>
                <Dropdown
                  value={kind}
                  options={kindOptions}
                  emptyLabel="Select"
                  onChange={(opt: any) =>
                    onKindChange((opt?.value as KindType) ?? '')
                  }
                />
              </FormField>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField label="Name" required>
                <Input
                  value={form.name}
                  onChange={e =>
                    onNameChange((e.target as HTMLInputElement).value)
                  }
                  placeholder="Component name"
                  required
                  isize="normal"
                />
              </FormField>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormField label="Owner" required>
                <Dropdown
                  value={form.owner}
                  options={ownerOptions}
                  emptyLabel="Select owner"
                  onChange={(opt: any) =>
                    onFieldChange('owner', opt?.value ?? '')
                  }
                />
              </FormField>
            </Grid>

            {/* Row 2 — Component Type / Domain / System */}
            <Grid item xs={12} md={4}>
              <FormField label="Component Type" required>
                <Dropdown
                  value={form.type}
                  options={componentTypeOptionsDisplay}
                  emptyLabel="Select"
                  onChange={(opt: any) => onFieldChange('type', opt?.value)}
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="Domain" required>
                <Dropdown
                  value={form.domain}
                  options={domainOptions}
                  emptyLabel="Select"
                  onChange={(opt: any) => onDomainChange(opt?.value)}
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="System" required>
                <Dropdown
                  value={form.system}
                  options={systemOptions}
                  emptyLabel="Select"
                  onChange={(opt: any) => onFieldChange('system', opt?.value)}
                />
              </FormField>
            </Grid>

            {/* Row 3 — Component extras */}
            <Grid item xs={12} md={4}>
              <FormField label="Tags (comma-separated)">
                <Input
                  value={form.tags}
                  onChange={e =>
                    onFieldChange('tags', (e.target as HTMLInputElement).value)
                  }
                  placeholder="tag1, tag2"
                  isize="normal"
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="Description">
                <Input
                  value={form.description}
                  onChange={e =>
                    onFieldChange(
                      'description',
                      (e.target as HTMLInputElement).value,
                    )
                  }
                  placeholder="Description"
                  isize="normal"
                />
              </FormField>
            </Grid>
          </>
        ) : (
          <>
            {/* Row 1 — Kind / Name / Owner */}
            <Grid item xs={12} md={4}>
              <FormField label="Kind" required>
                <Dropdown
                  value={kind}
                  options={kindOptions}
                  emptyLabel="Select"
                  onChange={(opt: any) =>
                    onKindChange((opt?.value as KindType) ?? '')
                  }
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="Name" required>
                <Input
                  value={form.name}
                  onChange={e =>
                    onNameChange((e.target as HTMLInputElement).value)
                  }
                  placeholder="API name"
                  required
                  isize="normal"
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="Owner" required>
                <Dropdown
                  value={form.owner}
                  options={ownerOptions}
                  emptyLabel="Select owner"
                  onChange={(opt: any) =>
                    onFieldChange('owner', opt?.value ?? '')
                  }
                />
              </FormField>
            </Grid>

            {/* Row 2 — Domain / System / API Type */}
            <Grid item xs={12} md={4}>
              <FormField label="Domain" required>
                <Dropdown
                  value={form.domain}
                  options={domainOptions}
                  emptyLabel="Select"
                  onChange={(opt: any) => onDomainChange(opt?.value)}
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="System" required>
                <Dropdown
                  value={form.system}
                  options={systemOptions}
                  emptyLabel="Select"
                  onChange={(opt: any) => onFieldChange('system', opt?.value)}
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="API Type" required>
                <Dropdown
                  value={form.apiType}
                  options={apiTypeOptions}
                  emptyLabel="Select"
                  onChange={(opt: any) => onFieldChange('apiType', opt?.value)}
                />
              </FormField>
            </Grid>

            {/* Row 3 — API Version / Tags / Description */}
            <Grid item xs={12} md={4}>
              <FormField label="API Version">
                <Input
                  value={form.apiVersion}
                  onChange={e =>
                    onFieldChange(
                      'apiVersion',
                      (e.target as HTMLInputElement).value,
                    )
                  }
                  placeholder="1.0.0"
                  isize="normal"
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="Tags (comma-separated)">
                <Input
                  value={form.tags}
                  onChange={e =>
                    onFieldChange('tags', (e.target as HTMLInputElement).value)
                  }
                  placeholder="tag1, tag2"
                  isize="normal"
                />
              </FormField>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormField label="Description">
                <Input
                  value={form.description}
                  onChange={e =>
                    onFieldChange(
                      'description',
                      (e.target as HTMLInputElement).value,
                    )
                  }
                  placeholder="Description"
                  isize="normal"
                />
              </FormField>
            </Grid>
          </>
        )}
      </Grid>
    </div>
  );
};
